import { type FunctionComponent, type HTMLAttributes } from 'react';
import { type ServerAnalyticsInterface } from '../../../../../../@types/build';
import { type ButtonSize, type ButtonStyle, StyledButton } from '../../../buttons';
import { StyledLink } from '../../../common-components/link/styled-link.component';

export type ActionButton = {
	label: string;
	onClick: () => void;
	onConfigureClick?: () => void;
	isConfigurable?: boolean;
	isThisTheProductBeingAddedToCart?: boolean;
	buttonStyle?: Extract<ButtonStyle, 'PRIMARY' | 'SECONDARY' | 'INTERNAL' | 'DANGER'>;
	buttonSize?: NonNullable<Extract<ButtonSize, 'DEFAULT' | 'SMALL' | 'LARGE'>>;
	automationHook?: string;
};

export type ActionLink = {
	name: string;
	url: string;
	openInNewTab?: boolean;
	automationHook?: string;
};

export type ProductCardActionsProps = {
	testId?: string;
	link?: ActionLink;
	action?: ActionButton;
	serverAnalytics?: ServerAnalyticsInterface;
	isBusy?: boolean;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductCardActions: FunctionComponent<ProductCardActionsProps> = ({
	className = 'items-center-ns justify-start-ns',
	testId = 'card-actions',
	link,
	action,
	serverAnalytics,
	isBusy
}) => {
	const { name, url, openInNewTab, automationHook: linkAutomationHook } = link || {};
	const {
		label,
		buttonStyle,
		buttonSize,
		automationHook: actionAutomationHook,
		onClick,
		onConfigureClick,
		isConfigurable,
		isThisTheProductBeingAddedToCart
	} = action || {};

	const handleOnClick = () => {
		if (isConfigurable && onConfigureClick) {
			onConfigureClick();
		} else if (onClick && !isConfigurable) {
			onClick();
		}
	};

	return (
		<div className={`flex flex-column items-end flex-row-ns ${className}`} data-testid={testId}>
			{link && url && (
				<StyledLink
					url={url}
					className="f6 mb3 mb0-ns"
					automationHook={linkAutomationHook}
					openInNewTab={openInNewTab}
					serverAnalytics={serverAnalytics}>
					{name}
				</StyledLink>
			)}
			{action && (
				<>
					<span className="br b--theme-grey-light mh3 h2 dn db-ns" />
					<StyledButton
						onClick={handleOnClick}
						buttonStyle={buttonStyle || 'PRIMARY'}
						size={buttonSize || 'SMALL'}
						automationHook={actionAutomationHook}
						isBusy={isBusy === undefined || isBusy === false ? false : true}
						busyText={isThisTheProductBeingAddedToCart ? 'Adding to cart...' : label}>
						{label}
					</StyledButton>
				</>
			)}
		</div>
	);
};
